import { Locale } from "next/router";

import { GetStaticPaths, GetStaticProps } from "next";

import { initializeApollo } from "../../../lib/apollo";
import getTranslations from "../../../lib/getTranslations";
import { HelpCategoryView } from "@/common/utils/help/help-category.utils";
import { getHelpArticlePath } from "@/common/utils/help/help-url.utils";
import { withLocale } from "@/common/utils/url.utils";
import { HeaderLanguageSwitcherProvider } from "@/components/Header/HeaderLanguageSwitcherContext";
import MainLayout from "@/components/Layouts/MainLayout";
import { HelpArticleView } from "@/features/help/HelpArticleView";
import {
  HelpArticleDetail,
  HelpArticleListItem,
  fetchAllHelpArticles,
  fetchHelpArticle,
  fetchHelpCategories,
} from "@/features/help/api";
import { HELP_REVALIDATE_SECONDS } from "@/features/help/help.static";
import { defaultLocale, locales, toContentLocale } from "@/i18n/config";

type HelpArticlePageProps = {
  article: HelpArticleDetail;
  articles: HelpArticleListItem[];
  categories: HelpCategoryView[];
  languageSwitcherUrls?: Partial<Record<Locale, string>>;
  hreflangUrls?: Record<Locale, string>;
};

const HelpArticlePage = ({
  article,
  articles,
  categories,
  hreflangUrls,
}: HelpArticlePageProps) => {
  return (
    <HelpArticleView
      article={article}
      articles={articles}
      categories={categories}
      hreflangUrls={hreflangUrls}
    />
  );
};

HelpArticlePage.getLayout = (
  page: JSX.Element,
  props: HelpArticlePageProps
) => {
  return (
    <HeaderLanguageSwitcherProvider
      value={{ urls: props.languageSwitcherUrls ?? {} }}
    >
      <MainLayout>{page}</MainLayout>
    </HeaderLanguageSwitcherProvider>
  );
};

export const getStaticProps: GetStaticProps<HelpArticlePageProps> = async ({
  locale,
  params,
}) => {
  const categorySlug = params?.categorySlug;
  const articleSlug = params?.articleSlug;

  if (typeof categorySlug !== "string" || typeof articleSlug !== "string") {
    return { notFound: true, revalidate: HELP_REVALIDATE_SECONDS };
  }

  const client = initializeApollo();
  const resolvedLocale = toContentLocale(locale);
  // A gateway/help-service failure throws HelpUnavailableError here on
  // purpose: Next then keeps serving the last good page (or answers 500 on a
  // first render) instead of caching notFound for HELP_REVALIDATE_SECONDS.
  const article = await fetchHelpArticle(client, articleSlug, resolvedLocale);

  if (!article || !article.categorySlug) {
    return { notFound: true, revalidate: HELP_REVALIDATE_SECONDS };
  }

  if (article.categorySlug !== categorySlug || article.slug !== articleSlug) {
    return {
      redirect: {
        destination: withLocale(
          getHelpArticlePath(article.categorySlug, article.slug),
          locale ?? defaultLocale,
          defaultLocale
        ),
        permanent: true,
      },
      revalidate: HELP_REVALIDATE_SECONDS,
    };
  }

  const [categories, articles] = await Promise.all([
    fetchHelpCategories(client, resolvedLocale),
    fetchAllHelpArticles(client, resolvedLocale, article.categorySlug),
  ]);

  const hreflangUrls = article.hreflang.reduce(
    (urls, alternate) => {
      urls[alternate.locale as Locale] = getHelpArticlePath(
        alternate.categorySlug,
        alternate.slug
      );
      return urls;
    },
    {} as Record<Locale, string>
  );

  // `en-us` reads the `en` article (WHO-5766): same path, its own prefix.
  for (const routedLocale of locales) {
    const contentPath = hreflangUrls[toContentLocale(routedLocale)];

    if (!hreflangUrls[routedLocale] && contentPath) {
      hreflangUrls[routedLocale] = contentPath;
    }
  }

  const languageSwitcherUrls = Object.fromEntries(
    Object.entries(hreflangUrls).map(([hreflangLocale, path]) => [
      hreflangLocale,
      withLocale(path, hreflangLocale, defaultLocale),
    ])
  );

  return {
    props: {
      ...(await getTranslations(locale)),
      article,
      articles,
      categories,
      hreflangUrls,
      languageSwitcherUrls,
    },
    revalidate: HELP_REVALIDATE_SECONDS,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export default HelpArticlePage;
